import React, { useEffect, useState } from 'react';
import { Building2, Heart, Boxes } from 'lucide-react';
import Navbar from '../components/Navbar';
import HeroSection from '../components/HeroSection';
import ServicesSection from '../components/ServicesSection';
import AboutSection from '../components/AboutSection';
import FeaturesSection from '../components/FeaturesSection';
import ContactSection from '../components/ContactSection';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { organizationSchema } from '../utils/structuredData';

const HomePage = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Matterport 3D Scanning UK | London, Manchester, Nationwide | Digital Scanning Group"
        description="UK-wide Matterport 3D scanning service. Point cloud surveys, scan to BIM, drone surveys and virtual tours. Trusted by NHS and FTSE 100. Get a free quote."
        keywords="matterport uk, 3d scanning uk, matterport london, matterport manchester, 3d scanning nationwide, point cloud survey uk, matterport virtual tour, laser scanning uk, digital twin uk, scan to bim, 3d laser scanning uk, matterport service provider, virtual tour uk, reality capture, building scanning, point cloud survey, revit bim modelling, drone surveys, construction scanning, NHS virtual tours, as-built surveys, heritage scanning, commercial property scanning"
        canonicalUrl="https://www.digitalscanninggroup.com/"
        structuredData={organizationSchema}
      />
      <Navbar scrolled={scrolled} />
      <HeroSection />
      <ServicesSection />
      <AboutSection />
      <FeaturesSection />
      {/* Case Studies Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-gray-800">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">Case Studies</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              Real results from real projects. See how our 3D scanning services have delivered measurable value for clients across healthcare, construction and commercial property.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Case Study 1 */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300">
              <div className="inline-flex p-3 bg-red-500/10 rounded-xl mb-5">
                <Heart className="h-7 w-7 text-red-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">NHS Neonatal Unit Virtual Tours</h3>
              <p className="text-sm text-blue-400 font-medium mb-4">NHS Trust (London)</p>
              <div className="space-y-3 text-gray-400 text-sm">
                <div>
                  <span className="text-gray-300 font-semibold">Challenge:</span> Parents unable to visit neonatal units before admission
                </div>
                <div>
                  <span className="text-gray-300 font-semibold">Solution:</span> Matterport 3D virtual tours of 4 NICU units
                </div>
                <div className="pt-3 border-t border-gray-700">
                  <span className="text-emerald-400 font-semibold">Result:</span> 89% reduction in parent anxiety scores, tours viewed 12,000+ times
                </div>
              </div>
            </div>

            {/* Case Study 2 */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300">
              <div className="inline-flex p-3 bg-blue-500/10 rounded-xl mb-5">
                <Building2 className="h-7 w-7 text-blue-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Tier 1 Contractor — Progress Monitoring</h3>
              <p className="text-sm text-blue-400 font-medium mb-4">Major contractor (confidential)</p>
              <div className="space-y-3 text-gray-400 text-sm">
                <div>
                  <span className="text-gray-300 font-semibold">Challenge:</span> Remote stakeholders needed real-time site visibility
                </div>
                <div>
                  <span className="text-gray-300 font-semibold">Solution:</span> Monthly Matterport scans + drone surveys over 18-month project
                </div>
                <div className="pt-3 border-t border-gray-700">
                  <span className="text-emerald-400 font-semibold">Result:</span> 40% reduction in site visits, disputes resolved 3x faster
                </div>
              </div>
            </div>

            {/* Case Study 3 */}
            <div className="bg-gray-800/50 border border-gray-700 rounded-2xl p-8 hover:border-blue-500/50 transition-all duration-300">
              <div className="inline-flex p-3 bg-amber-500/10 rounded-xl mb-5">
                <Boxes className="h-7 w-7 text-amber-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Commercial Office — Scan to BIM</h3>
              <p className="text-sm text-blue-400 font-medium mb-4">Property management firm, Central London</p>
              <div className="space-y-3 text-gray-400 text-sm">
                <div>
                  <span className="text-gray-300 font-semibold">Challenge:</span> No accurate as-built drawings for 8-floor office refurbishment
                </div>
                <div>
                  <span className="text-gray-300 font-semibold">Solution:</span> Full point cloud survey + LOD 300 Revit model
                </div>
                <div className="pt-3 border-t border-gray-700">
                  <span className="text-emerald-400 font-semibold">Result:</span> £180,000 saved in design rework, project delivered 6 weeks early
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactSection />
      <Footer />
    </div>
  );
};

export default HomePage;
